import { getDocs, query, orderBy, getDoc, where } from 'firebase/firestore';
import { getClientCollection, getClientDoc } from '../../../../../services/FirebaseService';

declare global {
    interface Window {
        jQuery: any;
        marquee: any;
        catalogImages: any[];
        Swiper: any;
        catalogDesktopSwiper?: any;
    }
}

export interface CatalogImageText {
    pt: string;
    en: string;
}

export interface CatalogImage {
    catalogImageUrl: string;
    catalogImageName: string;
    catalogImageOrder?: number;
    catalogImageText?: string | CatalogImageText;
    id?: string;
}

// Cache for catalog data to avoid refetching when navigating back to home page
interface CatalogCache {
    images: CatalogImage[];
    backgroundUrl: string | null;
    videoUrl: string | null;
    carouselEnabled: boolean;
    timestamp: number;
}

let catalogCache: CatalogCache | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache duration

// Check if cache is still valid
function isCacheValid(): boolean {
    if (!catalogCache) return false;
    const now = Date.now();
    return (now - catalogCache.timestamp) < CACHE_DURATION;
}

// Get cached catalog data
function getCachedCatalogData(): CatalogCache | null {
    if (isCacheValid()) {
        return catalogCache;
    }
    return null;
}

// Set cached catalog data
function setCachedCatalogData(images: CatalogImage[], backgroundUrl: string | null, videoUrl: string | null, carouselEnabled: boolean = true): void {
    catalogCache = {
        images,
        backgroundUrl,
        videoUrl,
        carouselEnabled,
        timestamp: Date.now()
    };
}

async function verifyImageUrl(url: string): Promise<boolean> {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            resolve(true);
        };
        img.onerror = () => {
            resolve(false);
        };
        img.src = url;
    });
}

// Preload images for better performance - preload ALL images immediately for desktop
function preloadImages(imageUrls: string[]): void {
    // Preload ALL images immediately for instant display (like mobile version)
    imageUrls.forEach(url => {
        const img = new Image();
        img.src = url;
        img.onerror = () => {
            // Silently handle errors
        };
    });
}

// Static "products mode" showcase — used when the catalog carousel is switched
// off from the myouweb admin (months with no catalog). Images live in
// public/assets/images/products/ on every site.
const PRODUCTS_MODE_BG = '/assets/images/products/products-bg.webp';
const PRODUCTS_MODE_IMAGES: CatalogImage[] = [1, 2, 3, 4, 5, 6, 7].map(n => ({
    id: `products-mode-${n}`,
    catalogImageUrl: `/assets/images/products/product-${n}.webp`,
    catalogImageName: `Product ${n}`,
    catalogImageOrder: n,
}));

// Swap the section into products mode: same carousel, product showcase images,
// products background. The catalog PDF modal is disabled via the
// window.catalogProductsMode flag (see CatalogSection.tsx / catalog-section.css).
function enterProductsMode(containerElement: HTMLElement): void {
    const bgEl = containerElement.querySelector('.catalog-section__right-bg') as HTMLElement | null;
    if (bgEl) {
        bgEl.style.backgroundImage = `url("${PRODUCTS_MODE_BG}")`;
        bgEl.classList.add('catalog-section__right-bg--loaded');
    }
}

async function loadCatalogData(
    setCatalogImages: (images: CatalogImage[]) => void,
    setVideoUrl: (url: string | null) => void,
    containerElement: HTMLElement
): Promise<void> {
    try {
        // Check cache first - if valid, use cached data immediately
        const cached = getCachedCatalogData();
        if (cached) {
            (window as any).catalogProductsMode = cached.carouselEnabled === false;
            containerElement.classList.toggle('catalog-section--products', cached.carouselEnabled === false);
            if (cached.carouselEnabled === false) {
                enterProductsMode(containerElement);
            }
            setCatalogImages(cached.images);
            setVideoUrl(cached.videoUrl);
            
            // Apply cached background immediately
            if (cached.backgroundUrl) {
                const bgEl = containerElement.querySelector('.catalog-section__right-bg') as HTMLElement;
                if (bgEl) {
                    bgEl.style.backgroundImage = `url("${cached.backgroundUrl}")`;
                    bgEl.classList.add('catalog-section__right-bg--loaded');
                }
            }

            // Preload images from cache
            const imageUrls = cached.images.map(img => img.catalogImageUrl).filter(Boolean);
            preloadImages(imageUrls);
            
            window.catalogImages = cached.images;
            document.dispatchEvent(new CustomEvent('catalogImagesLoaded', { detail: cached.images }));
            
            // Return early - no need to fetch from Firebase
            return;
        }
        
        
        // Load background, catalog images, and videos in parallel for faster loading
        const [bgDocResult, catalogImagesResult, videosResult] = await Promise.allSettled([
            getDoc(getClientDoc('settings', 'catalog')),
            getDocs(query(getClientCollection('catalogImages'), orderBy('catalogImageOrder', 'asc'))),
            (async () => {
                try {
                    const catalogQuery = query(
                        getClientCollection('videos'),
                        where('type', '==', 'catalog')
                    );
                    return await getDocs(catalogQuery);
                } catch (whereError) {
                    return await getDocs(getClientCollection('videos'));
                }
            })()
        ]);

        // Catalog carousel kill-switch — settings/catalog.carouselEnabled is set
        // from the myouweb admin panel; some months there is no catalog, so only
        // the carousel is hidden. The rest of the section (help text, phone,
        // video button, background photo) stays. Missing field counts as enabled.
        const carouselEnabled = !(bgDocResult.status === 'fulfilled' && bgDocResult.value.exists()
            && bgDocResult.value.data().carouselEnabled === false);
        (window as any).catalogProductsMode = !carouselEnabled;
        containerElement.classList.toggle('catalog-section--products', !carouselEnabled);
        if (!carouselEnabled) {
            enterProductsMode(containerElement);
        }

        // Handle background image - apply immediately, verify in background
        // (skipped in products mode — enterProductsMode set its own background)
        let backgroundUrl: string | null = null;
        if (carouselEnabled && bgDocResult.status === 'fulfilled' && bgDocResult.value.exists()) {
            const docData = bgDocResult.value.data();
            backgroundUrl = docData.backgroundUrl || null;

            if (backgroundUrl) {
                const bgEl = containerElement.querySelector('.catalog-section__right-bg') as HTMLElement;
                if (bgEl) {
                    // Apply background immediately without waiting for verification
                    bgEl.style.backgroundImage = `url("${backgroundUrl}")`;
                    bgEl.classList.add('catalog-section__right-bg--loaded');
                    // Verify in background and remove if invalid
                    verifyImageUrl(backgroundUrl).then(isAccessible => {
                        if (!isAccessible && bgEl) {
                            bgEl.style.backgroundImage = '';
                            bgEl.classList.remove('catalog-section__right-bg--loaded');
                        }
                    });
                }
            }
        }

        // Handle catalog images (skipped when the carousel is disabled, so the
        // mobile slider never initializes and nothing renders in the carousel)
        let images: CatalogImage[] = [];
        if (!carouselEnabled) {
            images = PRODUCTS_MODE_IMAGES;
            window.catalogImages = images;
            preloadImages(images.map(img => img.catalogImageUrl));
            setCatalogImages(images);
            document.dispatchEvent(new CustomEvent('catalogImagesLoaded', { detail: images }));
        } else if (catalogImagesResult.status === 'fulfilled') {
            try {
                images = catalogImagesResult.value.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                } as CatalogImage));

                window.catalogImages = images;

                // Preload all catalog images for instant display
                const imageUrls = images.map(img => img.catalogImageUrl).filter(Boolean);
                preloadImages(imageUrls);

                setCatalogImages(images);
                document.dispatchEvent(new CustomEvent('catalogImagesLoaded', { detail: images }));
            } catch (imagesError) {
                setCatalogImages([]);
            }
        } else {
            setCatalogImages([]);
        }

        // Handle videos
        let videoUrl: string | null = null;
        if (videosResult.status === 'fulfilled') {
            try {
                const videosSnap = videosResult.value;
                let allVideos: any[] = [];
                
                if (videosSnap.docs.length > 0) {
                    allVideos = videosSnap.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));
                }
                
                let catalogVideo = allVideos.find((video: any) => {
                    return video.type === 'catalog';
                });
                
                if (catalogVideo) {
                    const url = (catalogVideo as any).url || (catalogVideo as any).videoUrl || (catalogVideo as any).downloadURL;
                    if (url) {
                        videoUrl = url;
                        setVideoUrl(url);
                        
                        const catalogVideoLink = containerElement.querySelector('.catalog-video-link .catalog-video-popup') as HTMLElement;
                        if (catalogVideoLink) {
                            catalogVideoLink.setAttribute('href', 'javascript:void(0)');
                            catalogVideoLink.setAttribute('data-video-url', url);
                            catalogVideoLink.setAttribute('data-video-source', 'firebase');
                            // Check if it's a Facebook video
                            const isFacebookVideo = url.includes('facebook.com') || url.includes('fb.com');
                            catalogVideoLink.setAttribute('data-video-type', isFacebookVideo ? 'facebook' : 'other');
                        }
                    }
                }
            } catch (e) {
                // Video loading failed, continue without video
            }
        }
        
        // Cache the loaded data for future use
        setCachedCatalogData(images, backgroundUrl, videoUrl, carouselEnabled);
    } catch (error) {
        setCatalogImages([]);
    }
}

function initializeMarquee(slidingTextElement: HTMLElement | null): void {
    if (!slidingTextElement) {
        return;
    }

    const initMarquee = (): void => {
        const jQuery = (window as any).jQuery;
        if (typeof jQuery === 'undefined' || !jQuery.fn || typeof jQuery.fn.marquee === 'undefined') {
            setTimeout(initMarquee, 100);
            return;
        }

        try {
            const $element = jQuery(slidingTextElement);
            
            if ($element.data('marquee')) {
                try {
                    $element.marquee('destroy');
                } catch (destroyError) {
                }
            }
        
            $element.marquee({
                speed: 12,
                gap: 40,
                delayBeforeStart: 0,
                direction: 'left',
                duplicated: true,
                pauseOnHover: true,
                startVisible: true,
                allowCss3Support: true,
                // Ensure seamless infinite loop
                pauseOnCycle: false,
            });
        } catch (error) {
        }
    };

    setTimeout(() => {
        initMarquee();
    }, 500);
}

/**
 * Convert Facebook video URL to embed URL
 */
function getFacebookEmbedUrl(videoUrl: string): string {
    // Facebook embed format: https://www.facebook.com/plugins/video.php?href={encoded_url}&show_text=0&width=560
    const encodedUrl = encodeURIComponent(videoUrl);
    return `https://www.facebook.com/plugins/video.php?href=${encodedUrl}&show_text=0&width=560&height=315&appId`;
}

/**
 * Show centered video modal (not full-screen) with autoplay
 * Supports both direct video URLs and Facebook video embeds
 */
function showCenteredVideoModal(videoUrl: string, videoType?: string): void {
    // Remove existing modal if any
    const existingModal = document.getElementById('centeredVideoModal');
    if (existingModal) {
        existingModal.remove();
    }

    // Check if it's a Facebook video
    const isFacebookVideo = videoType === 'facebook' || videoUrl.includes('facebook.com') || videoUrl.includes('fb.com');

    // Detect mobile device
    const isMobile = window.innerWidth <= 768;
    const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024;

    // Create modal backdrop
    const modal = document.createElement('div');
    modal.id = 'centeredVideoModal';
    const modalPadding = isMobile ? '10px' : '20px';
    modal.style.cssText = `position:fixed; top:0; left:0; width:100%; height:100%; background:rgba(0,0,0,0.85); z-index:99999; display:flex; align-items:center; justify-content:center; animation:fadeIn 0.3s ease; padding:${modalPadding};`;

    // Create modal content container (centered, responsive)
    const modalContent = document.createElement('div');
    const modalWidth = isMobile ? '98%' : isTablet ? '85%' : '90%';
    const modalMaxWidth = isMobile ? '100%' : '900px';
    const borderRadius = isMobile ? '8px' : '12px';
    modalContent.style.cssText = `position:relative; width:${modalWidth}; max-width:${modalMaxWidth}; background:#000; border-radius:${borderRadius}; overflow:hidden; box-shadow:0 25px 50px rgba(0,0,0,0.7);`;

    // Create close button (smaller on mobile)
    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '&times;';
    const closeBtnSize = isMobile ? '32px' : '36px';
    const closeBtnFontSize = isMobile ? '24px' : '28px';
    const closeBtnTop = isMobile ? '8px' : '10px';
    const closeBtnRight = isMobile ? '8px' : '10px';
    closeBtn.style.cssText = `position:absolute; top:${closeBtnTop}; right:${closeBtnRight}; background:#ea2127; border:none; color:#fff; font-size:${closeBtnFontSize}; width:${closeBtnSize}; height:${closeBtnSize}; border-radius:50%; cursor:pointer; z-index:100001; display:flex; align-items:center; justify-content:center; transition:all 0.3s ease; box-shadow:0 4px 12px rgba(0,0,0,0.3); font-weight:bold;`;
    closeBtn.addEventListener('mouseenter', () => {
        closeBtn.style.background = '#c41e24';
        closeBtn.style.transform = 'scale(1.1) rotate(90deg)';
    });
    closeBtn.addEventListener('mouseleave', () => {
        closeBtn.style.background = '#ea2127';
        closeBtn.style.transform = 'scale(1) rotate(0deg)';
    });

    const closeModal = () => {
        modal.style.animation = 'fadeOut 0.3s ease';
        setTimeout(() => modal.remove(), 300);
        document.body.style.overflow = '';
        document.removeEventListener('keydown', escHandler);
    };

    closeBtn.addEventListener('click', closeModal);

    if (isFacebookVideo) {
        // Create iframe for Facebook video embed
        const iframe = document.createElement('iframe');
        const embedUrl = getFacebookEmbedUrl(videoUrl);
        iframe.src = embedUrl;
        iframe.width = '100%';
        // Responsive height: smaller on mobile, medium on tablet, full on desktop
        const iframeHeight = isMobile ? '280' : isTablet ? '400' : '500';
        iframe.height = iframeHeight;
        iframe.style.cssText = `width:100%; height:${iframeHeight}px; border:none; display:block; background:#000;`;
        iframe.setAttribute('scrolling', 'no');
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('allowTransparency', 'true');
        iframe.setAttribute('allow', 'encrypted-media');
        iframe.setAttribute('allowFullScreen', 'true');

        modalContent.appendChild(closeBtn);
        modalContent.appendChild(iframe);
    } else {
        // Create video element with autoplay for direct video URLs
    const video = document.createElement('video');
    video.src = videoUrl;
    video.controls = true;
    video.autoplay = true;
    video.playsInline = true;
    video.style.cssText = 'width:100%; height:auto; max-height:80vh; display:block; background:#000;';

    // Handle video errors
    video.addEventListener('error', () => {
        const errorMsg = document.createElement('div');
        errorMsg.style.cssText = 'padding:60px 40px; text-align:center; color:#fff; font-size:16px;';
        errorMsg.textContent = 'Error loading video. Please try again later.';
        modalContent.appendChild(errorMsg);
    });

    // Handle video load
    video.addEventListener('loadeddata', () => {
    });

    modalContent.appendChild(closeBtn);
    modalContent.appendChild(video);
    }

    modal.appendChild(modalContent);

    document.body.appendChild(modal);
    document.body.style.overflow = 'hidden';

    // Close on background click
    modal.addEventListener('click', (e) => {
        if (e.target === modal) {
            closeModal();
        }
    });

    // Close on ESC key
    const escHandler = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            closeModal();
        }
    };
    document.addEventListener('keydown', escHandler);

    // Add fade in/out animations
    const style = document.createElement('style');
    style.textContent = `
        @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        @keyframes fadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
        }
    `;
    if (!document.getElementById('videoModalStyles')) {
        style.id = 'videoModalStyles';
        document.head.appendChild(style);
    }
}

export function initializeCatalogVideoPopup(containerElement: HTMLElement): void {
    // Use a shorter delay and check periodically for video link
    const checkVideoLink = () => {
        const catalogVideoLink = containerElement.querySelector('.catalog-video-link .catalog-video-popup') as HTMLElement;
        if (catalogVideoLink) {
            const videoSource = catalogVideoLink.getAttribute('data-video-source');
            
            if (videoSource === 'firebase') {
                const jQuery = (window as any).jQuery;
                if (typeof jQuery !== 'undefined' && jQuery.fn && jQuery.fn.magnificPopup) {
                    jQuery(catalogVideoLink).off('click');
                    jQuery(catalogVideoLink).removeClass('video-popup');
                }
                
                const newCatalogLink = catalogVideoLink.cloneNode(true) as HTMLElement;
                catalogVideoLink.parentNode?.replaceChild(newCatalogLink, catalogVideoLink);
                
                // Add both click and touch event handlers for mobile
                const handleVideoClick = function(this: HTMLElement, e: Event) {
                    e.preventDefault();
                    e.stopPropagation();
                    const videoUrl = this.getAttribute('data-video-url') || (this as HTMLAnchorElement).href;
                    const videoType = this.getAttribute('data-video-type') || 'other';
                    
                    // Use centered video modal (not full-screen)
                    showCenteredVideoModal(videoUrl, videoType);
                };

                newCatalogLink.addEventListener('click', function(e) {
                    handleVideoClick.call(this, e);
                });
                // Add touch support for mobile
                newCatalogLink.addEventListener('touchstart', function(e) {
                    e.stopPropagation();
                }, { passive: true });
                newCatalogLink.addEventListener('touchend', function(e) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleVideoClick.call(newCatalogLink, e);
                }, { passive: false });
            } else {
                // For YouTube videos, use Magnific Popup
                const jQuery = (window as any).jQuery;
                if (typeof jQuery !== 'undefined' && jQuery.fn && jQuery.fn.magnificPopup) {
                    jQuery(catalogVideoLink).magnificPopup({
                        type: 'iframe',
                        mainClass: 'mfp-fade',
                        removalDelay: 160,
                        preloader: true,
                        fixedContentPos: false,
                        iframe: {
                            patterns: {
                                youtube: {
                                    index: 'youtube.com/',
                                    id: 'v=',
                                    src: 'https://www.youtube.com/embed/%id%?autoplay=1'
                                },
                                vimeo: {
                                    index: 'vimeo.com/',
                                    id: '/',
                                    src: 'https://player.vimeo.com/video/%id%?autoplay=1'
                                }
                            }
                        },
                        callbacks: {
                            open: function() {
                                document.body.classList.add('mfp-body-lock');
                                document.documentElement.classList.add('mfp-body-lock');
                            },
                            close: function() {
                                document.body.classList.remove('mfp-body-lock');
                                document.documentElement.classList.remove('mfp-body-lock');
                            }
                        }
                    });
                }
            }
        } else {
            // Retry after a short delay if video link not found yet
            setTimeout(checkVideoLink, 200);
        }
    };
    
    setTimeout(checkVideoLink, 100);
}

/**
 * Update pagination active state
 */
function updatePaginationActiveState(swiper: any): void {
    if (!swiper || !swiper.pagination) {
        return;
    }
    
    const activeIndex = swiper.activeIndex;
    const paginationEl = document.getElementById('catalog-swiper-pagination');
    
    if (!paginationEl) {
        return;
    }
    
    // Get all bullets from DOM (more reliable than swiper.pagination.bullets)
    const bullets = paginationEl.querySelectorAll('.swiper-pagination-bullet');
    
    bullets.forEach((bullet: Element, index: number) => {
        if (index === activeIndex) {
            bullet.classList.add('swiper-pagination-bullet-active');
        } else {
            bullet.classList.remove('swiper-pagination-bullet-active');
        }
    });
    
    // Also update swiper's bullets array if it exists
    if (swiper.pagination.bullets) {
        swiper.pagination.bullets.forEach((bullet: HTMLElement, index: number) => {
            if (index === activeIndex) {
                bullet.classList.add('swiper-pagination-bullet-active');
            } else {
                bullet.classList.remove('swiper-pagination-bullet-active');
            }
        });
    }
}

/**
 * Manually render pagination bullets based on actual catalog image count
 */
function renderCatalogPagination(swiper: any, imageCount: number): void {
    if (!swiper || !swiper.pagination || imageCount === 0) {
        return;
    }
    
    const paginationEl = document.getElementById('catalog-swiper-pagination');
    if (!paginationEl) {
        return;
    }
    
    // Clear existing bullets
    paginationEl.innerHTML = '';
    
    const activeIndex = swiper.activeIndex || 0;
    
    // Create bullets for each catalog image (not for loading slide)
    for (let i = 0; i < imageCount; i++) {
        const bullet = document.createElement('span');
        bullet.className = i === activeIndex 
            ? 'swiper-pagination-bullet swiper-pagination-bullet-active' 
            : 'swiper-pagination-bullet';
        bullet.setAttribute('data-index', i.toString());
        bullet.setAttribute('tabindex', '0');
        bullet.setAttribute('role', 'button');
        bullet.setAttribute('aria-label', `Go to slide ${i + 1}`);
        
        // Add click handler to navigate to the slide
        bullet.addEventListener('click', function() {
            if (swiper && !swiper.destroyed && swiper.slideTo) {
                try {
                    swiper.slideTo(i);
                } catch (e) {
                }
            }
        });
        
        paginationEl.appendChild(bullet);
    }
    
    // Update bullets array reference in swiper
    if (swiper.pagination) {
        swiper.pagination.bullets = Array.from(paginationEl.querySelectorAll('.swiper-pagination-bullet'));
    }
}

function initializeCatalogSwiper(): void {
    const initSwiper = (): void => {
        if (typeof window.Swiper === 'undefined') {
            setTimeout(initSwiper, 100);
            return;
        }

        setTimeout(() => {
            try {
                const carouselElement = document.querySelector('.catalog-mobile-swiper');
                if (!carouselElement) {
                    return;
                }

                // Check if swiper already exists and destroy it
                if ((carouselElement as any).swiper) {
                    (carouselElement as any).swiper.destroy(true, true);
                }

                // Create pagination element if it doesn't exist
                const existingPagination = document.getElementById('catalog-swiper-pagination');
                if (existingPagination) {
                    existingPagination.remove();
                }
                const paginationEl = document.createElement('div');
                paginationEl.id = 'catalog-swiper-pagination';
                paginationEl.className = 'swiper-pagination';
                carouselElement.appendChild(paginationEl);

                // Initialize Swiper with optimized config
                const catalogSwiper = new window.Swiper('.catalog-mobile-swiper', {
                    slidesPerView: 1,
                    spaceBetween: 0,
                    speed: 600,
                    loop: false,
                    observer: true,
                    observeParents: true,
                    watchSlidesProgress: true,
                    watchSlidesVisibility: true,
                    preloadImages: false, // Disable preloading to prevent freezing
                    lazy: {
                        loadPrevNext: true,
                        loadPrevNextAmount: 2,
                    },
                    updateOnWindowResize: true,
                    centeredSlides: false,
                    // Important: Add resistance and proper touch handling
                    resistanceRatio: 0.85,
                    threshold: 10,
                    touchRatio: 1,
                    touchAngle: 45,
                    // Prevent freezing at boundaries
                    allowTouchMove: true,
                    touchStartPreventDefault: false,
                    touchMoveStopPropagation: false,
                    // Better edge handling
                    edgeSwipeDetection: true,
                    edgeSwipeThreshold: 20,
                    pagination: {
                        el: '#catalog-swiper-pagination',
                        type: 'bullets',
                        clickable: true,
                        dynamicBullets: false,
                        renderBullet: function (_index: number, className: string) {
                            return '<span class="' + className + '"></span>';
                        }
                    },
                    on: {
                        init: function(this: any) {
                            // Ensure pagination is correct on initialization
                            setTimeout(() => {
                                // Get actual catalog image count (exclude loading slide)
                                const imageCount = window.catalogImages?.length || 0;
                                
                                // If we have images, manually render pagination
                                if (imageCount > 0) {
                                    renderCatalogPagination(this, imageCount);
                                    // Update active state after rendering
                                    setTimeout(() => {
                                        updatePaginationActiveState(this);
                                    }, 50);
                                }
                                
                                // Update to recalculate pagination (React will handle DOM updates)
                                if (this.update) {
                                    this.update();
                                }
                            }, 100);
                        },
                        // Reduce unnecessary updates - only update on critical events
                        slideChange: function(this: any) {
                            // Update pagination active state
                            updatePaginationActiveState(this);
                            
                            // Only update if needed
                            const slidesLength = this.slides?.length || 0;
                            const activeIndex = this.activeIndex;
                            
                            // Prevent getting stuck at the last slide
                            if (activeIndex >= slidesLength - 1) {
                                // Allow swiping back from last slide
                                this.allowSlidePrev = true;
                                this.allowSlideNext = false;
                            } else if (activeIndex === 0) {
                                // At first slide - ensure it's fully at the beginning
                                this.allowSlidePrev = false;
                                this.allowSlideNext = true;
                                // Ensure first slide is fully visible at the beginning
                                setTimeout(() => {
                                    if (this.activeIndex === 0 && 
                                        !this.destroyed && 
                                        this.slideTo && 
                                        this.slides && 
                                        this.slides.length > 0) {
                                        try {
                                            this.slideTo(0, 0);
                                        } catch (e) {
                                            // Silently handle errors
                                        }
                                    }
                                }, 50);
                            } else {
                                // In the middle
                                this.allowSlidePrev = true;
                                this.allowSlideNext = true;
                            }
                        },
                        transitionStart: function(this: any) {
                            // Update pagination during transition for smooth visual feedback
                            updatePaginationActiveState(this);
                        },
                        transitionEnd: function(this: any) {
                            // Ensure pagination is correct after transition
                            updatePaginationActiveState(this);
                        },
                        reachEnd: function(this: any) {
                            // Ensure we can swipe back when reaching the end
                            this.allowSlidePrev = true;
                            this.allowSlideNext = false;
                            updatePaginationActiveState(this);
                        },
                        reachBeginning: function(this: any) {
                            // At the beginning - ensure first slide is fully visible
                            this.allowSlidePrev = false;
                            this.allowSlideNext = true;
                            // Force slide to beginning to ensure it's fully visible
                            if (this.activeIndex === 0 && 
                                !this.destroyed && 
                                this.slideTo && 
                                this.slides && 
                                this.slides.length > 0) {
                                setTimeout(() => {
                                    if (!this.destroyed && this.slideTo) {
                                        try {
                                            this.slideTo(0, 0);
                                        } catch (e) {
                                            // Silently handle errors
                                        }
                                    }
                                }, 50);
                            }
                        }
                    },
                    breakpoints: {
                        0: {
                            slidesPerView: 1.15,
                            spaceBetween: 20,
                            centeredSlides: false,
                            // Ensure first slide starts at beginning
                            slidesOffsetBefore: 0,
                            // Add space after last slide to show it fully (approximately 15% of viewport)
                            slidesOffsetAfter: 60,
                        },
                        480: {
                            slidesPerView: 1.15,
                            spaceBetween: 20,
                            // Ensure first slide starts at beginning
                            slidesOffsetBefore: 0,
                            // Add space after last slide to show it fully
                            slidesOffsetAfter: 60,
                        },
                        768: {
                            slidesPerView: 1,
                            spaceBetween: 0,
                            slidesOffsetBefore: 0,
                            slidesOffsetAfter: 0,
                        },
                    },
                });

                if (catalogSwiper) {
                    // Store swiper instance globally for React component access
                    (window as any).catalogSwiper = catalogSwiper;
                    
                    // Add space after last slide to ensure it's fully visible
                    // Calculate offset based on viewport width (approximately 15% for 1.15 slidesPerView)
                    const calculateOffset = () => {
                        const viewportWidth = window.innerWidth || carouselElement.clientWidth;
                        const offset = Math.round(viewportWidth * 0.15);
                        return offset;
                    };
                    
                    // Set offset after Swiper initializes
                    setTimeout(() => {
                        if (catalogSwiper && catalogSwiper.params) {
                            const offset = calculateOffset();
                            // Try to set slidesOffsetAfter if supported
                            if (catalogSwiper.params.breakpoints) {
                                const breakpoint = catalogSwiper.params.breakpoints[0] || catalogSwiper.params.breakpoints['0'];
                                if (breakpoint) {
                                    breakpoint.slidesOffsetAfter = offset;
                                    // Ensure first slide can reach beginning
                                    breakpoint.slidesOffsetBefore = 0;
                                }
                            }
                            // Update swiper to apply changes
                            if (catalogSwiper.update && !catalogSwiper.destroyed) {
                                try {
                                    catalogSwiper.update();
                                } catch (e) {
                                }
                            }
                            // Ensure first slide is at the beginning
                            if (catalogSwiper.activeIndex === 0 && 
                                !catalogSwiper.destroyed && 
                                catalogSwiper.slideTo && 
                                catalogSwiper.slides && 
                                catalogSwiper.slides.length > 0) {
                                try {
                                    catalogSwiper.slideTo(0, 0);
                                } catch (e) {
                                    // Silently handle errors
                                }
                            }
                        }
                    }, 150);
                    
                    // Single initial update after a short delay
                    setTimeout(() => {
                        if (catalogSwiper && 
                            !catalogSwiper.destroyed && 
                            catalogSwiper.update) {
                            try {
                                catalogSwiper.update();
                            } catch (e) {
                            }
                        }
                    }, 100);
                    
                    // Listen for catalog images loaded event to update swiper
                    document.addEventListener('catalogImagesLoaded', (event: any) => {
                        setTimeout(() => {
                            // Check if swiper is properly initialized and not destroyed
                            if (catalogSwiper && 
                                !catalogSwiper.destroyed && 
                                catalogSwiper.update && 
                                catalogSwiper.slides) {
                                
                                // Get actual catalog image count from the event or window
                                const imageCount = event?.detail?.length || window.catalogImages?.length || 0;
                                
                                // Manually render pagination with correct count (before update)
                                if (imageCount > 0) {
                                    renderCatalogPagination(catalogSwiper, imageCount);
                                }
                                
                                // Force update swiper to recalculate everything
                                // React will handle removing the loading slide from DOM
                                try {
                                    catalogSwiper.update();
                                } catch (e) {
                                }
                                
                                // Update pagination active state after update
                                if (imageCount > 0) {
                                    setTimeout(() => {
                                        updatePaginationActiveState(catalogSwiper);
                                    }, 50);
                                }
                                
                                // Only slideTo if swiper is ready
                                if (catalogSwiper.slideTo && catalogSwiper.activeIndex !== undefined) {
                                    try {
                                        catalogSwiper.slideTo(0, 0);
                                    } catch (e) {
                                        // Silently handle slideTo errors
                                    }
                                }
                            }
                        }, 300);
                    }, { once: true });
                    
                    // Optimized image load handling - only update on first and last image
                    const swiperWrapper = carouselElement.querySelector('.swiper-wrapper');
                    if (swiperWrapper) {
                        const images = swiperWrapper.querySelectorAll('img');
                        let loadedCount = 0;
                        const totalImages = images.length;
                        
                        // Shorter timeout for faster initial display
                        const forceUpdateTimeout = setTimeout(() => {
                            if (catalogSwiper && 
                                !catalogSwiper.destroyed && 
                                catalogSwiper.update) {
                                try {
                                    catalogSwiper.update();
                                } catch (e) {
                                }
                            }
                        }, 2000); // Force update after 2 seconds max
                        
                        images.forEach((img) => {
                            const handleLoad = () => {
                                loadedCount++;
                                img.classList.add('loaded');
                                
                                // Only update on first image load and when all images are loaded
                                if (loadedCount === 1 || loadedCount === totalImages) {
                                    if (catalogSwiper && 
                                        !catalogSwiper.destroyed && 
                                        catalogSwiper.update) {
                                        try {
                                            catalogSwiper.update();
                                        } catch (e) {
                                        }
                                    }
                                }
                                
                                // If last image, ensure swiper is fully updated
                                if (loadedCount === totalImages) {
                                    clearTimeout(forceUpdateTimeout);
                                }
                            };
                            
                            const handleError = () => {
                                loadedCount++;
                                // Continue even if image fails to load
                                if (loadedCount === totalImages) {
                                    clearTimeout(forceUpdateTimeout);
                                    if (catalogSwiper && 
                                        !catalogSwiper.destroyed && 
                                        catalogSwiper.update) {
                                        try {
                                            catalogSwiper.update();
                                        } catch (e) {
                                        }
                                    }
                                }
                            };
                            
                            if (img.complete && img.naturalHeight > 0) {
                                handleLoad();
                            } else {
                                img.addEventListener('load', handleLoad, { once: true });
                                img.addEventListener('error', handleError, { once: true });
                            }
                        });
                        
                        // If no images, clear timeout
                        if (totalImages === 0) {
                            clearTimeout(forceUpdateTimeout);
                        }
                    }
                }
            } catch (error) {
            }
        }, 100);
    };

    // Initialize swiper immediately if Swiper is available, otherwise retry
    initSwiper();
}

/**
 * Initialize desktop Swiper for catalog images
 * Similar to mobile but for desktop with mouse drag support
 */
function initializeDesktopCatalogSwiper(): void {
    // Only run on desktop screens
    if (window.innerWidth <= 991) {
        return;
    }

    const initSwiper = (): void => {
        if (typeof window.Swiper === 'undefined') {
            setTimeout(initSwiper, 100);
            return;
        }

        setTimeout(() => {
            try {
                const swiperElement = document.querySelector('.catalog-desktop-swiper');
                if (!swiperElement) {
                    return;
                }

                // Wait for actual image slides to be rendered in DOM
                const slides = swiperElement.querySelectorAll('.swiper-slide img');
                if (slides.length === 0) {
                    // Images not rendered yet, wait
                    setTimeout(initSwiper, 200);
                    return;
                }

                // Check if swiper already exists and destroy it
                if ((swiperElement as any).swiper) {
                    (swiperElement as any).swiper.destroy(true, true);
                }

                // Initialize Swiper with same pattern as mobile
                const catalogSwiper = new window.Swiper('.catalog-desktop-swiper', {
                    slidesPerView: 1,
                    slidesPerGroup: 1,
                    spaceBetween: 0,
                    speed: 600,
                    loop: true,
                    initialSlide: 0,
                    observer: true,
                    observeParents: true,
                    watchSlidesProgress: true,
                    watchSlidesVisibility: true,
                    updateOnWindowResize: true,
                    centeredSlides: false,
                    resistanceRatio: 0.85,
                    threshold: 10,
                    touchRatio: 1,
                    touchAngle: 45,
                    allowTouchMove: true,
                    touchStartPreventDefault: false,
                    touchMoveStopPropagation: false,
                    edgeSwipeDetection: true,
                    edgeSwipeThreshold: 20,
                    navigation: {
                        nextEl: '.catalog-nav-next',
                        prevEl: '.catalog-nav-prev',
                    },
                    keyboard: {
                        enabled: true,
                        onlyInViewport: true,
                    },
                    on: {
                        init: function(this: any) {
                            this.update();
                        },
                        slideChange: function(this: any) {
                            document.dispatchEvent(new CustomEvent('catalogDesktopSlideChange', { detail: this.realIndex }));
                        },
                    }
                });

                (window as any).catalogDesktopSwiper = catalogSwiper;

                // Update swiper when images load
                const swiperWrapper = swiperElement.querySelector('.swiper-wrapper');
                if (swiperWrapper) {
                    const images = swiperWrapper.querySelectorAll('img');
                    let loadedCount = 0;
                    const totalImages = images.length;
                    
                    images.forEach((img) => {
                        const handleLoad = () => {
                            loadedCount++;
                            if (loadedCount === 1 || loadedCount === totalImages) {
                                if (catalogSwiper && !catalogSwiper.destroyed && catalogSwiper.update) {
                                    try {
                                        catalogSwiper.update();
                                    } catch (e) {
                                    }
                                }
                            }
                        };
                        
                        if (img.complete && (img as HTMLImageElement).naturalHeight > 0) {
                            handleLoad();
                        } else {
                            img.addEventListener('load', handleLoad, { once: true });
                            img.addEventListener('error', handleLoad, { once: true });
                        }
                    });
                }
            } catch (error) {
            }
        }, 100);
    };

    // Start initialization
    initSwiper();
}

export function initializeCatalogSection(
    containerElement: HTMLElement,
    slidingTextElement: HTMLElement | null,
    setCatalogImages: (images: CatalogImage[]) => void,
    setVideoUrl: (url: string | null) => void,
    _setCurrentPage: (page: number) => void,
    _setIsModalOpen: (open: boolean) => void,
    _setModalCurrentPage: (page: number) => void
): void {
    if (!containerElement) {
        return;
    }

    // Start loading data immediately - no delay
    loadCatalogData(setCatalogImages, setVideoUrl, containerElement);

    // Initialize marquee with minimal delay
    setTimeout(() => {
        initializeMarquee(slidingTextElement);
    }, 100);

    initializeCatalogVideoPopup(containerElement);
    
    // Initialize mobile swiper carousel
    initializeCatalogSwiper();
    
    // Initialize desktop swiper carousel
    initializeDesktopCatalogSwiper();
}

